/**
 * Portão de rota da Central — decide se `/conquistas`, `/gamification`,
 * `/ranking` e `/gamification/history` EXISTEM para este documento.
 *
 * 07/08 (auditoria prod-vs-main): em produção a gamificação está desligada e
 * esses quatro caminhos davam 404 — honesto. Na `main` eles passaram a montar
 * cabeçalho ("Central de Conquistas") e bottom-nav incondicionalmente, com o
 * corpo gated: tela branca com título. Pior que 404.
 *
 * Por que 404 e não redirect: com o flag mestre desligado a rota não existe
 * para ninguém, e é isso que a tela deve dizer. Um redirect para `/` inventa a
 * história de que a página "mudou de lugar" e ainda brigaria com o
 * `MobileConquestsAuthority`, que faz o caminho inverso (`?conquistas=` → rota)
 * quando o acesso está liberado.
 *
 * Por que um portão e não remover a rota do registro: `routes.generated.ts` é
 * composto em BUILD por `scripts/compose-routes-plugin.ts`, enquanto a flag é
 * um valor de runtime que chega depois da primeira pintura. Desregistrar
 * exigiria remontar o roteador quando a flag virasse — perdendo o estado do
 * app — e ainda apostaria no palpite persistido do localStorage, que o próprio
 * `gamification-flag.ts` proíbe usar como autoridade. O portão entrega o mesmo
 * que o usuário vê em produção hoje (a página 404 canônica) sem montar nada de
 * gamificação: sem cabeçalho, sem bottom-nav e sem consulta ao servidor.
 */
import { useEffect } from "react";
import { Outlet, useSearchParams } from "react-router-dom";
import NotFoundPage from "@/features/_404/pages/NotFoundPage";
import { useConquestsCentralReachable } from "./conquests-central-reachable";
import { withoutConquestsSearchParams } from "./v3/conquests-url";
import { useGamificationFlagsAuthoritative } from "./useGamificationEnabled";

type ConquestsRouteStatus = "off" | "on" | "pending";

/** Rota da Central sob o flag mestre: 404 quando desligado, `Outlet` quando
 * ligado, tela neutra enquanto o boot não respondeu.
 * @example `{ Component: ConquestsMasterRouteGate, children: [...] }` */
export default function ConquestsMasterRouteGate(): React.JSX.Element {
  const status = useConquestsRouteStatus();
  if (status === "pending") return <ConquestsRoutePending />;
  if (status === "off") return <ConquestsRouteClosed />;
  return <Outlet />;
}

/** 404 do flag mestre desligado, sem deixar a query da Central emboscada.
 *
 * O 404 sozinho não basta. Se `?conquistas=competir` sobrevive na URL, ele fica
 * esperando: o app revalida as capabilities no `focus`, e no instante em que o
 * operador liga o mestre a rota deixa de dar 404 e a Central **abre sozinha** na
 * área que a query pedia — sem ninguém ter clicado. É o que o cenário de toggle
 * do #245 chama de "query revogada esperando um master ON para reabrir"
 * (`refactored-mobile-conquests-access-scenarios.ts`, `disableMobileMaster`).
 *
 * Limpa só no ramo `off`, nunca no `pending`: antes da resposta autoritativa um
 * deep-link legítimo ainda pode ser honrado, e apagá-lo ali perderia o destino
 * de quem tem acesso. `replace` porque isto não é navegação — o usuário não
 * pediu para sair de lugar nenhum e o Voltar não pode cair na mesma URL suja.
 */
function ConquestsRouteClosed(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const cleaned = withoutConquestsSearchParams(searchParams);
    if (cleaned.toString() === searchParams.toString()) return;
    setSearchParams(cleaned, { replace: true });
  }, [searchParams, setSearchParams]);
  return <NotFoundPage />;
}

function useConquestsRouteStatus(): ConquestsRouteStatus {
  const reachable = useConquestsCentralReachable();
  const authoritative = useGamificationFlagsAuthoritative();
  // Sem resposta autoritativa ainda não dá para dizer 404 nem para pintar a
  // Central: piscar uma coisa e depois a outra é pior que esperar o boot.
  if (!authoritative) return "pending";
  return reachable ? "on" : "off";
}

function ConquestsRoutePending(): React.JSX.Element {
  return (
    <div
      data-testid="conquests-route-pending"
      className="min-h-dvh bg-background"
    />
  );
}
